import axios from "axios";
import { toast } from "material-react-toastify";

export const sanitizePhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return '0000000000'; // Return 10 zeros as fallback
  }
  // Remove all non-digit characters (special symbols, whitespace, etc.)
  const sanitized = phone.replace(/\D/g, '');
  if (sanitized === '') {
    return '0000000000';
  }
  return sanitized;
};

const COMMON_URL = `${process.env.REACT_APP_SPRING_API_URL}`;
const GETHOTELIMAGESURL = "user/groupBranding";
const SETHOTELIMAGESURL = "common/set-group-whitelabel-config";
const USERID = "core/api/v1/image/update-user-id-images";
const GUESTSID = "core/api/v1/image/update-guests-id-images";
const ISHAPRIMARYDOC = "core/api/v1/image/upload-isha-primary-attachments";
const ISHAOTHERDOC = "core/api/v1/image/upload-isha-other-guests-attachments";
const GETISHADOCS = "core/api/v1/image/get-isha-guest-documents";
const GETUSERID = "core/api/v1/image/get-user-id-images";
const UPDATEHOTELLOGO = "core/api/v1/image/update-hotel-logo";

export const getAllImagesAndUrl = async () => {
  return axios
    .get(
      COMMON_URL +
        GETHOTELIMAGESURL +
        "?hostUrl=" +
        `${window.location.hostname}`
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const setLogoAndFavicon = async (data) => {
  return axios
    .post(COMMON_URL + SETHOTELIMAGESURL, data)
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const setBookingUserIdImages = async (data) => {
  const sanitizedData = {
    ...data,
    name: data.name || "test",
    emailId: data.emailId || "test@stayflexi.com",
    phone: sanitizePhone(data.phone)
  };

  return axios
    .post(COMMON_URL + USERID, sanitizedData)
    .then((resp) => resp.data)
    .catch((err) => {
      toast.error(err.response.data);
    });
};

export const moveUserIdImages = async (hotelId, oldName, oldPhone, oldEmail, newName, newPhone, newEmail) => {
  const sanitizePhoneNumberForIdUpload = (phone) => {
    if (!phone || phone.trim() === '') {
      return '0000000000';
    }
    // Remove all non-digit characters (special symbols, whitespace, etc.)
    const sanitized = phone.replace(/\D/g, '');
    if (sanitized === '') {
      return '0000000000';
    }
    return sanitized;
  };

  if (oldName === newName && oldPhone === newPhone && oldEmail === newEmail) {
    return Promise.resolve();
  }

  try {
    // Get user id images data using the existing getUserId function
    const userImages = await getUserId(oldEmail, oldName, oldPhone, "true");

    // Upload to new location if images exist
    if (userImages?.frontUrl || userImages?.backUrl || userImages?.passportImageUrl) {
      console.log('Moving user ID images from old to new customer details');

      // Upload to new location using existing setBookingUserIdImages function
      const uploadTask = await setBookingUserIdImages({
        hotelId: hotelId,
        emailId: newEmail || "test@stayflexi.com",
        name: newName,
        phone: sanitizePhoneNumberForIdUpload(newPhone),
        frontData: userImages?.frontBas64Data ? userImages?.frontBas64Data : null,
        frontType: 'image/jpeg',
        backData: userImages?.backBase64Data ? userImages?.backBase64Data : null,
        backType: 'image/jpeg',
        passportImageData: userImages?.passportImageBase64 ? userImages?.passportImageBase64 : null,
        passportImageType: 'image/jpeg',
      });

      // Delete from old location by uploading empty data to overwrite
      try {
        await setBookingUserIdImages({
          hotelId: hotelId,
          emailId: oldEmail || "test@stayflexi.com",
          name: oldName,
          phone: sanitizePhoneNumberForIdUpload(oldPhone),
          frontData: null,
          frontType: 'image/jpeg',
          backData: null,
          backType: 'image/png',
          passportImageData: null,
          passportImageType: 'image/jpeg',
        });
      } catch (deleteError) {
        console.error('Error clearing old user images:', deleteError);
      }

      return uploadTask;
    }
  } catch (error) {
    console.error('Error moving user ID images:', error);
  }
};

export const setOtherGuestImages = async (data) => {
  const sanitizedData = {
    ...data,
    otherGuestIdProofs: data.otherGuestIdProofs.map(guest => ({
      ...guest,
      name: guest.name || "test",
      emailId: guest.emailId || "test@stayflexi.com",
      phone: sanitizePhone(guest.phone)
    }))
  };

  return axios
    .post(COMMON_URL + GUESTSID, sanitizedData)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const setIshaGuestDocument = async (data) => {
  return axios
    .post(COMMON_URL + ISHAPRIMARYDOC, data)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const setIshaOtherGuestDocuments = async (data) => {
  return axios
    .post(COMMON_URL + ISHAOTHERDOC, data)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const getIshaDocuments = async (emailId) => {
  return axios
    .get(COMMON_URL + GETISHADOCS + "?emailId=" + `${emailId}`)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};
export const setIshaUserImages = async (data, apiKey) => {
  return axios
    .post(
      "https://isha-in-api.stayflexi.com/web-api/isha/update-user-ID-images",
      data,
      {
        headers: {
          "X-SF-API-KEY": apiKey,
        },
      }
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const getUserId = async (emailId, name, phone, base64="") => {
  const sanitizedPhone = sanitizePhone(phone);
  const sanitizedEmail = emailId || "test@stayflexi.com";
  const sanitizedName = name || "test";
  const base64Param = base64 ? `&base64Format=${base64}` : "";

  return axios
    .get(COMMON_URL + GETUSERID + "?emailId=" + sanitizedEmail + "&name=" + sanitizedName + "&phone=" + sanitizedPhone + base64Param)
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};
export const getUserIdWithHotelId = async (emailId, hotelId, name, phone) => {
  const sanitizedPhone = sanitizePhone(phone);
  const sanitizedEmail = emailId || "test@stayflexi.com";
  const sanitizedName = name || "test";

  return axios
    .get(
      COMMON_URL +
        GETUSERID +
        "?emailId=" +
        sanitizedEmail +
        "&hotelId=" +
        hotelId +
        "&hotel_id=" +
        hotelId +
        "&name=" +
        sanitizedName +
        "&phone=" +
        sanitizedPhone
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const getUserIdIsha = async (emailId, hotelId) => {
  return axios
    .get(
      COMMON_URL +
        "web-api/isha/get-user-id-images" +
        "?emailId=" +
        emailId +
        "&hotelId=" +
        hotelId
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const setHotelLogo = async (data) => {
  return axios
    .post(COMMON_URL + UPDATEHOTELLOGO, data)
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};
